import { useState, useEffect } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Monitor, Users, Globe, Smartphone, Package, Edit, Save, X, Loader2, AlertTriangle } from "lucide-react";
import { type NetworkRule } from "@/hooks/useNetworkRules";
import { useScopeDevices, useScopeLevels } from "@/hooks/useScopeData";
import { useUpdateRuleScope } from "@/hooks/useUpdateRuleScope";
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";

interface RuleScopeConfigurationProps {
  rule: NetworkRule;
}

type ScopeType = "all" | "specific";

export const RuleScopeConfiguration = ({ rule }: RuleScopeConfigurationProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [scopeType, setScopeType] = useState<ScopeType>("all");
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const { toast } = useToast();

  // Validate rule ID before proceeding
  const isValidRuleId = rule?.id && rule.id.length >= 30 && rule.id.startsWith('k');

  console.log('RuleScopeConfiguration - rule ID:', rule?.id, 'isValid:', isValidRuleId);

  // Use Convex queries directly for devices and levels in this rule
  // Temporarily disabled due to backend returning Promise objects
  // TODO: Fix backend function devices/list:withRule to await all promises
  const devicesInRuleQuery = {
    status: "LoadingFirstPage" as const,
    results: [],
    isLoading: true
  };

  const levelsWithRuleData = useConvexQuery(
    api.levels.list.withRule,
    isValidRuleId ? { 
      rule: rule.id as any,
      paginationOpts: { cursor: null as any, numItems: 100 }
    } : "skip"
  );

  // Get all available devices and levels for editing
  const { data: allDevices = [], isLoading: isLoadingAllDevices } = useScopeDevices();
  const { data: allLevels = [], isLoading: isLoadingAllLevels } = useScopeLevels();
  const updateScopeMutation = useUpdateRuleScope();

  // Extract current devices and levels from the API responses
  const currentDevicesInRule = devicesInRuleQuery.results || [];
  const currentLevelsWithRule = (levelsWithRuleData as any)?.page || [];
  
  const isLoadingCurrentScope = devicesInRuleQuery.status === "LoadingFirstPage" || levelsWithRuleData === undefined;

  console.log('RuleScopeConfiguration - devices in rule:', currentDevicesInRule.length);
  console.log('RuleScopeConfiguration - levels with rule:', currentLevelsWithRule.length);

  // Handle invalid rule ID
  if (!isValidRuleId) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="text-center space-y-4">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Invalid Rule</h3>
              <p className="text-gray-600 mt-2">
                This rule has an invalid ID format and cannot be configured.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Initialize state from current scope data
  useEffect(() => {
    if (!isLoadingCurrentScope) {
      const hasSpecificScope = currentDevicesInRule.length > 0 || currentLevelsWithRule.length > 0;
      setScopeType(hasSpecificScope ? "specific" : "all");
      
      // Set selected items based on current API data
      setSelectedDevices(currentDevicesInRule.map((device: any) => device._id));
      setSelectedLevels(currentLevelsWithRule.map((level: any) => level._id));
    }
  }, [isLoadingCurrentScope, currentDevicesInRule, currentLevelsWithRule]);

  const getScopeAllText = () => {
    if (rule.type === "App Specific") {
      return "Scope to everyone who has this app";
    } else if (rule.apps && rule.apps.length > 0 && rule.apps[0].bundleId !== rule.apps[0].appName) {
      return "Scope to everyone who has this bundle ID";
    } else {
      return "Scope to every device";
    }
  };

  const getScopeIcon = () => {
    if (rule.type === "App Specific") {
      return <Smartphone className="w-4 h-4 text-purple-600" />;
    } else if (rule.apps && rule.apps.length > 0 && rule.apps[0].bundleId !== rule.apps[0].appName) {
      return <Package className="w-4 h-4 text-green-600" />;
    } else {
      return <Globe className="w-4 h-4 text-blue-600" />;
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!isValidRuleId) {
      toast({
        title: "Error",
        description: "Invalid rule ID format",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateScopeMutation.mutateAsync({
        ruleId: rule.id,
        selectedDeviceIds: scopeType === "specific" ? selectedDevices : [],
        selectedLevelIds: scopeType === "specific" ? selectedLevels : []
      });
      
      setIsEditing(false);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleCancel = () => {
    // Reset to current scope values
    if (!isLoadingCurrentScope) {
      const hasSpecificScope = currentDevicesInRule.length > 0 || currentLevelsWithRule.length > 0;
      setScopeType(hasSpecificScope ? "specific" : "all");
      setSelectedDevices(currentDevicesInRule.map((device: any) => device._id));
      setSelectedLevels(currentLevelsWithRule.map((level: any) => level._id));
    } else {
      setScopeType("all");
      setSelectedDevices([]);
      setSelectedLevels([]);
    }
    setIsEditing(false);
  };

  const handleDeviceSelect = (deviceId: string, checked: boolean) => {
    if (!isEditing) return;
    
    if (checked) {
      setSelectedDevices([...selectedDevices, deviceId]);
    } else {
      setSelectedDevices(selectedDevices.filter(id => id !== deviceId));
    }
  };

  const handleLevelSelect = (levelId: string, checked: boolean) => {
    if (!isEditing) return;
    
    if (checked) {
      setSelectedLevels([...selectedLevels, levelId]);
    } else {
      setSelectedLevels(selectedLevels.filter(id => id !== levelId));
    }
  };

  // Filter to show only selected items when not editing
  const filteredDevices = isEditing ? allDevices : allDevices.filter(device => selectedDevices.includes(device.id));
  const filteredLevels = isEditing ? allLevels : allLevels.filter(level => selectedLevels.includes(level.id));

  // Determine if we have any scope configured
  const hasConfiguredScope = currentDevicesInRule.length > 0 || currentLevelsWithRule.length > 0;
  const currentScopeType = hasConfiguredScope ? "specific" : "all";

  const isLoading = isLoadingAllDevices || isLoadingAllLevels || isLoadingCurrentScope;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="ml-2">Loading scope configuration...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>Scope Configuration</span>
          </CardTitle>
          
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  className="flex items-center space-x-2"
                  disabled={updateScopeMutation.isPending}
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  className="flex items-center space-x-2"
                  disabled={updateScopeMutation.isPending}
                >
                  {updateScopeMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>Save</span>
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={handleEdit}
                className="flex items-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Scope Type Selection - Only show when editing */}
        {isEditing && (
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="scope-all"
                checked={scopeType === "all"}
                onCheckedChange={(checked) => setScopeType(checked ? "all" : "specific")}
              />
              <Label htmlFor="scope-all" className="flex items-center space-x-2 text-sm font-medium">
                {getScopeIcon()}
                <span>{getScopeAllText()}</span>
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="scope-specific"
                checked={scopeType === "specific"}
                onCheckedChange={(checked) => setScopeType(checked ? "specific" : "all")}
              />
              <Label htmlFor="scope-specific" className="flex items-center space-x-2 text-sm font-medium">
                <Monitor className="w-4 h-4 text-orange-600" />
                <span>Scope to specific devices</span>
              </Label>
            </div>
          </div>
        )}

        {/* Current Scope Display */}
        {!isEditing && (
          <>
            {currentScopeType === "all" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-blue-800">
                  {getScopeIcon()}
                  <span className="font-medium">Global Scope</span>
                </div>
                <p className="text-sm text-blue-700 mt-1">
                  This rule applies to all relevant devices based on the rule type.
                </p>
              </div>
            )}

            {currentScopeType === "specific" && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-orange-800">
                  <Monitor className="w-4 h-4" />
                  <span className="font-medium">Targeted Scope</span>
                </div>
                <p className="text-sm text-orange-700 mt-1">
                  This rule targets {currentDevicesInRule.length} device(s) and {currentLevelsWithRule.length} level(s)
                </p>
              </div>
            )}
          </>
        )}

        {/* Specific Devices/Levels Selection - Show when editing OR when we have a specific scope */}
        {(isEditing || currentScopeType === "specific") && scopeType === "specific" && (
          <div className="mt-6">
            <Tabs defaultValue="devices" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="devices">
                  Devices ({isEditing ? selectedDevices.length : currentDevicesInRule.length})
                </TabsTrigger>
                <TabsTrigger value="levels">
                  Levels ({isEditing ? selectedLevels.length : currentLevelsWithRule.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="devices" className="mt-4">
                <div className="border rounded-lg">
                  {filteredDevices.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Monitor className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p>{isEditing ? "No devices available" : "No devices in scope"}</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {isEditing && <TableHead className="w-12">Select</TableHead>}
                          <TableHead>Device Name</TableHead>
                          <TableHead>Model</TableHead>
                          <TableHead>Serial Number</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredDevices.map((device) => (
                          <TableRow key={device.id}>
                            {isEditing && (
                              <TableCell>
                                <Checkbox
                                  checked={selectedDevices.includes(device.id)}
                                  onCheckedChange={(checked) => handleDeviceSelect(device.id, checked as boolean)}
                                />
                              </TableCell>
                            )}
                            <TableCell className="font-medium">{device.name}</TableCell>
                            <TableCell>{device.model || "Unknown"}</TableCell>
                            <TableCell>{device.serial_number || "N/A"}</TableCell>
                            <TableCell>
                              <Badge variant={device.status === "active" ? "default" : "secondary"}>
                                {device.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="levels" className="mt-4">
                <div className="border rounded-lg">
                  {filteredLevels.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p>{isEditing ? "No levels available" : "No levels in scope"}</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {isEditing && <TableHead className="w-12">Select</TableHead>}
                          <TableHead>Level Name</TableHead>
                          <TableHead>Devices</TableHead>
                          <TableHead>Description</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredLevels.map((level) => (
                          <TableRow key={level.id}>
                            {isEditing && (
                              <TableCell>
                                <Checkbox
                                  checked={selectedLevels.includes(level.id)}
                                  onCheckedChange={(checked) => handleLevelSelect(level.id, checked as boolean)}
                                />
                              </TableCell>
                            )}
                            <TableCell className="font-medium">{level.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{level.deviceCount} devices</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {level.description || "No description"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* Summary when editing */}
        {isEditing && scopeType === "specific" && (selectedDevices.length > 0 || selectedLevels.length > 0) && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 text-orange-800">
              <Monitor className="w-4 h-4" />
              <span className="font-medium">Targeted Scope</span>
            </div>
            <p className="text-sm text-orange-700 mt-1">
              Selected {selectedDevices.length} device(s) and {selectedLevels.length} level(s)
            </p>
          </div>
        )}

        {!isEditing && currentScopeType === "all" && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">
              Click the "Edit" button to configure scope targeting for this rule.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
